import { useAccountContext } from '@seek/hirer-graphql-react';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Columns,
  Column,
  Heading,
  Hidden,
  Stack,
  Inline,
} from 'braid-design-system';
import type React from 'react';

import { CreateJobButton } from 'src/App/components/CreateJobButton/CreateJobButton';
import { SearchBar } from 'src/App/components/SearchBar/SearchBar';
import { Skeleton } from 'src/App/components/Skeleton/Skeleton';

import translations from './.vocab';

interface JobSearchPanelProps {
  isDraft?: boolean;
  totalCount?: number;
  loading?: boolean;
}

export const JobSearchPanel: React.FC<
  React.PropsWithChildren<JobSearchPanelProps>
> = ({ isDraft = false, totalCount, loading = false }) => {
  const { t } = useTranslations(translations);
  const { accountContext } = useAccountContext();

  const totalCountMessageKey = isDraft ? 'total draft jobs' : 'total job ads';

  const showCreateJobButton =
    accountContext?.currentAdvertiser?.permissionOptions.canCreateJobs;

  const JobCount = () => (
    <Heading level="2" data={{ testid: 'totalCount' }}>
      <Skeleton loading={loading} numberOfCharacters={14}>
        {totalCount !== undefined && t(totalCountMessageKey, { totalCount })}
      </Skeleton>
    </Heading>
  );

  const CreateJobButtonWithLoading = () => (
    <Skeleton loading={loading} numberOfCharacters={23} height={48}>
      {showCreateJobButton && <CreateJobButton />}
    </Skeleton>
  );

  const MobileAndTabletView = () => (
    <Stack space="medium">
      <Columns space="medium" collapseBelow="tablet" alignY="center">
        <Column>
          <JobCount />
        </Column>
        <Column width="content">
          <CreateJobButtonWithLoading />
        </Column>
      </Columns>
      <SearchBar />
    </Stack>
  );

  const DesktopView = () => (
    <Stack space="medium">
      <JobCount />
      <Columns space="medium" alignY="center">
        <Column>
          <Box style={{ width: 516 }}>
            <SearchBar />
          </Box>
        </Column>
        <Column>
          <Inline space="none" align="right">
            <CreateJobButtonWithLoading />
          </Inline>
        </Column>
      </Columns>
    </Stack>
  );

  return (
    <Box paddingTop="xlarge">
      <Hidden above="tablet">
        <MobileAndTabletView />
      </Hidden>

      <Hidden below="desktop">
        <DesktopView />
      </Hidden>
    </Box>
  );
};
